/**
 * UCM (Used Car Manager) — design preview / future-state demo.
 *
 * This page is NOT live. It exists so Steve can show Arran what the planned
 * AutoTrader integration will look like, without committing to a build date.
 * All figures are illustrative, all CTAs are inert (or show a "coming soon"
 * tooltip). The "PREVIEW · DESIGN CONCEPT" banner at the top makes the
 * vision-vs-product line absolutely clear so nobody mistakes this for a
 * shipping feature.
 */
import { useState } from "react";
import {
  ExternalLink, TrendingUp, TrendingDown, Minus, Sparkles, Car, Clock,
  PoundSterling, Eye, ArrowRight, Tag, AlertTriangle, Zap,
} from "lucide-react";

// Illustrative stock list — typical RRG Toyota / Lexus used inventory.
// Each car has an asking price + a benchmark drawn from "AutoTrader" market median.
const STOCK = [
  { id: "1", reg: "YH22 TOY", make: "Toyota",  model: "Yaris Hybrid Excel",            year: 2022, miles: 24380, asking: 17995, market: 17200, listings: 138, days: 12, action: "reduce" },
  { id: "2", reg: "CT23 OYT", make: "Toyota",  model: "Corolla Touring Sports Design", year: 2023, miles: 18120, asking: 24495, market: 24800, listings: 84,  days: 6,  action: "hold"   },
  { id: "3", reg: "RV23 PVH", make: "Toyota",  model: "RAV4 Plug-in Hybrid Dynamic",   year: 2023, miles: 12410, asking: 38995, market: 41200, listings: 47,  days: 3,  action: "raise"  },
  { id: "4", reg: "CR22 HRT", make: "Toyota",  model: "C-HR Excel",                    year: 2022, miles: 28560, asking: 21495, market: 20400, listings: 112, days: 28, action: "reduce" },
  { id: "5", reg: "AX24 YGO", make: "Toyota",  model: "Aygo X",                        year: 2024,  miles: 8420, asking: 15495, market: 15600, listings: 76,  days: 9,  action: "hold"   },
  { id: "6", reg: "NX23 LXS", make: "Lexus",   model: "NX 450h+ Premium",              year: 2023, miles: 15240, asking: 49995, market: 51800, listings: 22,  days: 4,  action: "raise"  },
  { id: "7", reg: "UX22 LFS", make: "Lexus",   model: "UX 250h F Sport",               year: 2022, miles: 32110, asking: 27995, market: 26400, listings: 31,  days: 41, action: "reduce" },
  { id: "8", reg: "IS21 LXS", make: "Lexus",   model: "IS 300h",                       year: 2021, miles: 41280, asking: 22495, market: 22300, listings: 18,  days: 15, action: "hold"   },
];

// Customers currently in the service bay whose car's market value
// has shifted enough to make a trade-up conversation worth having.
const HOT_LEADS = [
  { customer: "Mrs Williams",   car: "2020 Toyota Yaris Excel",             curMarket: 11400, suggested: "2023 Yaris Hybrid Design",   uplift: 5800,  prob: "HIGH" },
  { customer: "Mr Henderson",   car: "2019 Toyota Auris Touring Sports",    curMarket: 9800,  suggested: "2022 Corolla Hybrid Icon",   uplift: 12600, prob: "MED"  },
  { customer: "Miss Bennett",   car: "2021 Lexus UX 250h",                  curMarket: 23200, suggested: "2024 Lexus UX 300e Premium", uplift: 14200, prob: "HIGH" },
];

function GBP(n) {
  return "\u00a3" + Math.round(n).toLocaleString("en-GB");
}

function actionStyle(action) {
  if (action === "reduce") return { Icon: TrendingDown, color: "text-warning", label: "REDUCE \u00a3500" };
  if (action === "raise")  return { Icon: TrendingUp,   color: "text-success", label: "RAISE \u00a3700"   };
  return { Icon: Minus, color: "text-muted-foreground", label: "HOLD" };
}

function priceDeltaPct(asking, market) {
  return ((asking - market) / market) * 100;
}

function UCMBanner() {
  return (
    <div className="rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 flex items-center gap-3" data-testid="ucm-preview-banner">
      <Sparkles className="w-5 h-5 text-warning shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm tracking-tight text-warning">
          PREVIEW &middot; DESIGN CONCEPT
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          This is what the AutoTrader integration will look like. Live data, recommendations and CTAs are inert in this preview. Powered by AutoTrader Connect (planned).
        </div>
      </div>
      <span className="text-xs label-overline text-warning shrink-0 hidden md:inline">COMING Q4 2026</span>
    </div>
  );
}

function KPIStrip() {
  const items = [
    { label: "Stock units",        value: STOCK.length,                  accent: "text-primary" },
    { label: "Avg days on lot",    value: Math.round(STOCK.reduce((s, c) => s + c.days, 0) / STOCK.length), accent: "text-foreground" },
    { label: "Below market",       value: STOCK.filter((c) => priceDeltaPct(c.asking, c.market) < -1).length, accent: "text-success" },
    { label: "Above market",       value: STOCK.filter((c) => priceDeltaPct(c.asking, c.market) > 1).length,  accent: "text-warning" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {items.map((k) => (
        <div key={k.label} className="card-default p-5" data-testid={`ucm-kpi-${k.label.toLowerCase().replace(/ /g,'-')}`}>
          <div className="label-overline">{k.label}</div>
          <div className={`font-display text-3xl font-bold mt-2 tracking-tight ${k.accent}`}>{k.value}</div>
        </div>
      ))}
    </div>
  );
}

function StockTable() {
  return (
    <div className="card-default overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr className="text-left">
            <th className="px-5 py-3 label-overline">Reg / vehicle</th>
            <th className="px-5 py-3 label-overline hidden md:table-cell">Year / miles</th>
            <th className="px-5 py-3 label-overline text-right">Asking</th>
            <th className="px-5 py-3 label-overline text-right hidden sm:table-cell">Market avg</th>
            <th className="px-5 py-3 label-overline text-right hidden lg:table-cell">vs market</th>
            <th className="px-5 py-3 label-overline text-right hidden lg:table-cell">Listings</th>
            <th className="px-5 py-3 label-overline text-right">Days</th>
            <th className="px-5 py-3 label-overline">Action</th>
          </tr>
        </thead>
        <tbody>
          {STOCK.map((c) => {
            const { Icon, color, label } = actionStyle(c.action);
            const delta = priceDeltaPct(c.asking, c.market);
            const deltaPctLabel = `${delta > 0 ? "+" : ""}${delta.toFixed(1)}%`;
            return (
              <tr key={c.id} className="border-t border-border hover:bg-muted/20 transition-colors" data-testid={`ucm-stock-row-${c.id}`}>
                <td className="px-5 py-4">
                  <div className="font-mono-plex text-xs bg-muted text-foreground inline-block px-1.5 py-0.5 rounded">{c.reg}</div>
                  <div className="font-semibold mt-1">{c.make} {c.model}</div>
                </td>
                <td className="px-5 py-4 hidden md:table-cell text-muted-foreground">
                  {c.year}<br /><span className="text-xs">{c.miles.toLocaleString("en-GB")} mi</span>
                </td>
                <td className="px-5 py-4 text-right font-mono-plex font-bold">{GBP(c.asking)}</td>
                <td className="px-5 py-4 text-right font-mono-plex text-muted-foreground hidden sm:table-cell">{GBP(c.market)}</td>
                <td className={`px-5 py-4 text-right font-mono-plex font-bold hidden lg:table-cell ${delta > 1 ? "text-warning" : delta < -1 ? "text-success" : "text-muted-foreground"}`}>{deltaPctLabel}</td>
                <td className="px-5 py-4 text-right font-mono-plex text-muted-foreground hidden lg:table-cell">{c.listings}</td>
                <td className="px-5 py-4 text-right font-mono-plex">{c.days}d</td>
                <td className="px-5 py-4">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${color}`}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function HotLeads() {
  return (
    <div className="card-default p-5" data-testid="ucm-hot-leads">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
          <Zap className="w-5 h-5 text-warning" /> Service-to-sales hot leads
        </h3>
        <span className="label-overline">CARS ON THE RAMP TODAY</span>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Cars currently in your service bay whose market value has moved enough to make a trade-up conversation worth having.
      </p>
      <div className="space-y-2">
        {HOT_LEADS.map((lead, i) => (
          <div key={i} className="border border-border rounded-md p-3 flex items-center gap-3 hover:border-foreground/30 transition-colors" data-testid={`ucm-lead-row-${i}`}>
            <div className="w-10 h-10 rounded-full bg-primary/15 text-primary grid place-items-center shrink-0 font-display font-bold">
              {lead.customer.split(" ").pop()[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{lead.customer}</div>
              <div className="text-xs text-muted-foreground truncate">
                Driving: <span className="text-foreground">{lead.car}</span> &middot; trade-in {GBP(lead.curMarket)}
              </div>
              <div className="text-xs mt-1">
                <span className="text-muted-foreground">Trade-up to:</span>{" "}
                <span className="text-foreground font-semibold">{lead.suggested}</span>
                <span className="text-success font-bold ml-1">(+{GBP(lead.uplift)} upgrade)</span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className={`text-xs font-bold px-2 py-0.5 rounded ${lead.prob === "HIGH" ? "bg-success/15 text-success" : "bg-warning/15 text-warning"}`}>
                {lead.prob} INTENT
              </div>
              <button
                disabled
                className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-muted-foreground border border-border rounded px-2 py-1 opacity-50 cursor-not-allowed"
                title="Coming Q4 2026"
                data-testid={`ucm-lead-notify-${i}`}
              >
                Notify sales <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", icon: Car,           t: "Used stock auto-syncs", b: "RRG inventory pulled overnight from your DMS \u2014 no manual entry." },
    { n: "02", icon: ExternalLink,  t: "AutoTrader benchmarks", b: "Every car priced against live market median + days-to-sell forecast." },
    { n: "03", icon: PoundSterling, t: "Re-price recommendations", b: "Each morning your team sees which cars need a tweak \u2014 with the data behind it." },
    { n: "04", icon: Zap,           t: "Service drive \u2192 hot lead", b: "Customer's car already on the ramp? SSP knows what it's worth. Sales team gets the lead before collection." },
  ];
  return (
    <div className="card-default p-5">
      <h3 className="font-display font-bold text-lg tracking-tight mb-1">How the integration will work</h3>
      <p className="text-sm text-muted-foreground mb-4">Four hooks. Two of them already exist in ServiceSell PRO today.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {steps.map(({ n, icon: Icon, t, b }) => (
          <div key={n} className="border border-border rounded-md p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono-plex text-primary font-bold">{n}</span>
              <Icon className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="font-bold text-sm">{t}</div>
            <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{b}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function UsedCarManagerTab() {
  return (
    <div className="space-y-5" data-testid="ucm-tab">
      <UCMBanner />

      <div>
        <h2 className="font-display font-bold text-2xl tracking-tight">Used Car Manager</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Live AutoTrader market data, RRG used stock, and service-drive trade-up leads &mdash; one view.
        </p>
      </div>

      <KPIStrip />

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-xl tracking-tight">Used stock &middot; live market position</h3>
          <span className="text-xs label-overline text-warning">DEMO DATA</span>
        </div>
        <StockTable />
      </div>

      <HotLeads />

      <HowItWorks />

      <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-5 text-center">
        <Sparkles className="w-6 h-6 text-primary mx-auto" />
        <div className="font-display font-bold text-lg mt-2 tracking-tight">This is the horizon, not the founding pilot.</div>
        <div className="text-sm text-muted-foreground mt-1 max-w-xl mx-auto">
          The Used Car Manager unlocks once the service-side data is flowing through Keyloop. We design it now so we ship it the moment the pilot proves out.
        </div>
      </div>
    </div>
  );
}
